import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, Download, Paperclip, Save, Trash2 } from "lucide-react";
import { useTranslation } from "@/shared/providers/LanguageProvider";
import { usePageActions } from "@/shared/hooks/usePageActions";
import { ApiError } from "@/shared/lib/apiClient";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { FormField, inputClass, selectClass, textareaClass } from "../components/FormField";
import {
  createAdminCompanyBilling,
  deleteAdminCompanyBillingAttachment,
  downloadAdminCompanyBillingAttachment,
  fetchAdminCompanyBillingStatement,
  fetchAdminConventionsMeta,
  fetchAdminPackagesMeta,
  updateAdminCompanyBilling,
  uploadAdminCompanyBillingAttachments,
  type AdminCompanyBillingAttachment,
  type AdminCompanyBillingPayload,
} from "../api/adminApi";
import { getAuthToken } from "../auth";

type FormState = {
  company_id: string;
  label: string;
  reference_no: string;
  provider_id: string;
  hospital_id: string;
  nature_facture: "forfait_mensuel" | "hors_forfait";
  periode_debut: string;
  periode_fin: string;
  nb_familles: string;
  tarif_unitaire_usd: string;
  montant_usd: string;
  description: string;
  status: "draft" | "published" | "paid" | "cancelled";
};

const emptyForm: FormState = {
  company_id: "",
  label: "",
  reference_no: "",
  provider_id: "",
  hospital_id: "",
  nature_facture: "forfait_mensuel",
  periode_debut: "",
  periode_fin: "",
  nb_familles: "",
  tarif_unitaire_usd: "",
  montant_usd: "",
  description: "",
  status: "draft",
};

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} Ko`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
}

export function CompanyBillingFormPage({ mode }: { mode: "create" | "edit" }) {
  const { t } = useTranslation();
  const { showToast } = usePageActions();
  const navigate = useNavigate();
  const params = useParams();
  const [searchParams] = useSearchParams();
  const linkedInvoiceId = Number(searchParams.get("invoice_id") || 0);
  const statementId = mode === "edit" ? Number(params.id) : null;

  const [form, setForm] = useState<FormState>(emptyForm);
  const [companies, setCompanies] = useState<Array<{ id: number; name: string }>>([]);
  const [providers, setProviders] = useState<Array<{ id: number; commercial_name: string }>>([]);
  const [hospitals, setHospitals] = useState<Array<{ id: number; name: string }>>([]);
  const [attachments, setAttachments] = useState<AdminCompanyBillingAttachment[]>([]);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [loading, setLoading] = useState(mode === "edit");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const setField = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((current) => ({ ...current, [key]: value }));
  };

  useEffect(() => {
    if (!getAuthToken()) return;

    let cancelled = false;
    void (async () => {
      try {
        const [convMeta, pkgMeta] = await Promise.all([
          fetchAdminConventionsMeta(),
          fetchAdminPackagesMeta(),
        ]);
        if (cancelled) return;
        setCompanies(convMeta.companies ?? []);
        setProviders(
          (convMeta.providers ?? []).map((p) => ({
            id: p.id,
            commercial_name: p.commercial_name,
          })),
        );
        setHospitals(pkgMeta.hospitals ?? []);
      } catch {
        /* meta vide */
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (mode !== "edit" || !statementId || Number.isNaN(statementId)) {
      setLoading(false);
      return;
    }
    if (!getAuthToken()) {
      setError("Connexion admin requise.");
      setLoading(false);
      return;
    }

    let cancelled = false;
    void (async () => {
      try {
        const row = await fetchAdminCompanyBillingStatement(statementId);
        if (cancelled) return;
        setForm({
          company_id: String(row.company_id),
          label: row.label,
          reference_no: row.reference_no ?? "",
          provider_id: row.provider_id ? String(row.provider_id) : "",
          hospital_id: row.hospital_id ? String(row.hospital_id) : "",
          nature_facture: row.nature_facture === "hors_forfait" ? "hors_forfait" : "forfait_mensuel",
          periode_debut: row.periode_debut ?? "",
          periode_fin: row.periode_fin ?? "",
          nb_familles: row.nb_familles != null ? String(row.nb_familles) : "",
          tarif_unitaire_usd: row.tarif_unitaire_usd ? String(row.tarif_unitaire_usd) : "",
          montant_usd: String(row.montant_usd),
          description: row.description ?? "",
          status: (row.status as FormState["status"]) || "draft",
        });
        setAttachments(row.attachments ?? []);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "Relevé introuvable.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [mode, statementId]);

  const computedAmount = useMemo(() => {
    if (form.nature_facture !== "forfait_mensuel") return null;
    const nb = Number(form.nb_familles);
    const tarif = Number(form.tarif_unitaire_usd);
    if (nb > 0 && tarif > 0) return Math.round(nb * tarif * 100) / 100;
    return null;
  }, [form.nature_facture, form.nb_familles, form.tarif_unitaire_usd]);

  const buildPayload = (): AdminCompanyBillingPayload | null => {
    const companyId = Number(form.company_id);
    const montant = computedAmount ?? Number(form.montant_usd);
    if (!form.label.trim() || !companyId || !montant || montant <= 0) {
      showToast({ message: "Entreprise, libellé et montant sont obligatoires.", variant: "warning" });
      return null;
    }

    return {
      company_id: companyId,
      label: form.label.trim(),
      montant_usd: montant,
      nature_facture: form.nature_facture,
      reference_no: form.reference_no.trim() || undefined,
      provider_id: form.provider_id ? Number(form.provider_id) : undefined,
      hospital_id: form.hospital_id ? Number(form.hospital_id) : undefined,
      periode_debut: form.periode_debut || undefined,
      periode_fin: form.periode_fin || undefined,
      nb_familles: form.nb_familles ? Number(form.nb_familles) : undefined,
      tarif_unitaire_usd: form.tarif_unitaire_usd ? Number(form.tarif_unitaire_usd) : undefined,
      description: form.description.trim() || undefined,
      status: form.status,
      ...(mode === "create" && linkedInvoiceId > 0
        ? { internal_invoice_id: linkedInvoiceId }
        : {}),
    };
  };

  const handleDeleteAttachment = async (attachmentId: number) => {
    if (!getAuthToken()) return;
    try {
      await deleteAdminCompanyBillingAttachment(attachmentId);
      setAttachments((current) => current.filter((a) => a.id !== attachmentId));
      showToast({ message: "Pièce jointe supprimée.", variant: "success" });
    } catch (err) {
      showToast({
        message: err instanceof Error ? err.message : "Suppression impossible.",
        variant: "warning",
      });
    }
  };

  const handleSubmit = async () => {
    const payload = buildPayload();
    if (!payload) return;

    if (!getAuthToken()) {
      navigate("/dashboard/company-billing");
      return;
    }

    setSaving(true);
    try {
      let savedId = statementId;
      if (mode === "edit" && statementId) {
        await updateAdminCompanyBilling(statementId, payload);
        showToast({ message: "Relevé mis à jour.", variant: "success" });
      } else {
        const created = await createAdminCompanyBilling(payload);
        savedId = created.id;
        showToast({ message: "Relevé créé.", variant: "success" });
      }

      if (savedId && pendingFiles.length > 0) {
        const upload = await uploadAdminCompanyBillingAttachments(savedId, pendingFiles);
        if (upload.errors?.length) {
          showToast({
            message: upload.errors.join(" "),
            variant: "warning",
          });
        } else {
          showToast({
            message: upload.message ?? `${upload.uploaded} pièce(s) jointe(s) ajoutée(s).`,
            variant: "success",
          });
        }
      }

      navigate("/dashboard/company-billing");
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : "Enregistrement impossible.";
      showToast({ message, variant: "warning" });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="theme-card p-8 text-center text-sm text-muted">Chargement…</div>;
  }

  return (
    <>
      <Breadcrumbs
        homeHref="/dashboard/admin"
        items={[
          { label: t.safecheck.nav.companyBilling, href: "/dashboard/company-billing" },
          { label: mode === "edit" ? "Modifier le relevé" : t.safecheck.nav.createCompanyBilling },
        ]}
      />

      {error && <p className="mb-3 text-sm text-danger">{error}</p>}

      <div className="theme-card p-6">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <FormField label="Entreprise" required>
            <select
              value={form.company_id}
              onChange={(e) => setField("company_id", e.target.value)}
              className={selectClass}
            >
              <option value="">— Sélectionner —</option>
              {companies.map((c) => (
                <option key={c.id} value={String(c.id)}>
                  {c.name}
                </option>
              ))}
            </select>
          </FormField>
          <FormField label="Libellé" required>
            <input
              value={form.label}
              onChange={(e) => setField("label", e.target.value)}
              className={inputClass}
              placeholder="Facture forfait Mars 2026"
            />
          </FormField>
          <FormField label="Référence">
            <input
              value={form.reference_no}
              onChange={(e) => setField("reference_no", e.target.value)}
              className={inputClass}
            />
          </FormField>
          <FormField label="Prestataire">
            <select
              value={form.provider_id}
              onChange={(e) => setField("provider_id", e.target.value)}
              className={selectClass}
            >
              <option value="">— Optionnel —</option>
              {providers.map((p) => (
                <option key={p.id} value={String(p.id)}>
                  {p.commercial_name}
                </option>
              ))}
            </select>
          </FormField>
          <FormField label="Hôpital">
            <select
              value={form.hospital_id}
              onChange={(e) => setField("hospital_id", e.target.value)}
              className={selectClass}
            >
              <option value="">— Optionnel —</option>
              {hospitals.map((h) => (
                <option key={h.id} value={String(h.id)}>
                  {h.name}
                </option>
              ))}
            </select>
          </FormField>
          <FormField label="Nature">
            <select
              value={form.nature_facture}
              onChange={(e) =>
                setField("nature_facture", e.target.value as FormState["nature_facture"])
              }
              className={selectClass}
            >
              <option value="forfait_mensuel">Forfait mensuel</option>
              <option value="hors_forfait">Hors forfait</option>
            </select>
          </FormField>
          <FormField label="Période début">
            <input
              type="date"
              value={form.periode_debut}
              onChange={(e) => setField("periode_debut", e.target.value)}
              className={inputClass}
            />
          </FormField>
          <FormField label="Période fin">
            <input
              type="date"
              value={form.periode_fin}
              onChange={(e) => setField("periode_fin", e.target.value)}
              className={inputClass}
            />
          </FormField>
          <FormField label="Nb patients / familles">
            <input
              type="number"
              min={0}
              value={form.nb_familles}
              onChange={(e) => setField("nb_familles", e.target.value)}
              className={inputClass}
            />
          </FormField>
          <FormField label="Tarif unitaire (USD)">
            <input
              type="number"
              min={0}
              step="0.01"
              value={form.tarif_unitaire_usd}
              onChange={(e) => setField("tarif_unitaire_usd", e.target.value)}
              className={inputClass}
            />
          </FormField>
          <FormField label="Montant total (USD)" required>
            <input
              type="number"
              min={0.01}
              step="0.01"
              value={computedAmount != null ? String(computedAmount) : form.montant_usd}
              onChange={(e) => setField("montant_usd", e.target.value)}
              readOnly={computedAmount != null}
              className={inputClass}
            />
          </FormField>
          <FormField label="Statut">
            <select
              value={form.status}
              onChange={(e) => setField("status", e.target.value as FormState["status"])}
              className={selectClass}
            >
              <option value="draft">Brouillon</option>
              <option value="published">Publié</option>
              <option value="paid">Payé</option>
              <option value="cancelled">Annulé</option>
            </select>
          </FormField>
          <div className="md:col-span-2">
            <FormField label="Description">
              <textarea
                value={form.description}
                onChange={(e) => setField("description", e.target.value)}
                className={textareaClass}
                rows={3}
              />
            </FormField>
          </div>

          <div className="md:col-span-2 rounded-lg border border-border p-4">
            <div className="mb-3 flex items-center gap-2 text-sm font-medium text-foreground">
              <Paperclip size={15} />
              Pièces jointes
            </div>
            <input
              type="file"
              multiple
              accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx"
              onChange={(e) => setPendingFiles(Array.from(e.target.files ?? []))}
              className="block w-full text-sm text-muted-foreground file:mr-3 file:rounded-md file:border-0 file:bg-primary file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-white"
            />
            {pendingFiles.length > 0 && (
              <p className="mt-2 text-xs text-muted">
                {pendingFiles.length} fichier(s) prêt(s) à l’envoi à l’enregistrement.
              </p>
            )}
            {attachments.length > 0 && (
              <ul className="mt-4 space-y-2">
                {attachments.map((att) => (
                  <li
                    key={att.id}
                    className="flex flex-wrap items-center justify-between gap-2 rounded-md bg-card-muted px-3 py-2 text-sm"
                  >
                    <span className="min-w-0 truncate text-foreground">
                      {att.original_name}
                      <span className="ml-2 text-xs text-muted">
                        ({formatFileSize(att.file_size)})
                      </span>
                    </span>
                    <span className="flex items-center gap-2">
                      <button
                        type="button"
                        className="inline-flex items-center gap-1 text-primary hover:underline"
                        onClick={() =>
                          void downloadAdminCompanyBillingAttachment(
                            att.id,
                            att.original_name,
                          ).catch((err: unknown) =>
                            showToast({
                              message:
                                err instanceof Error ? err.message : "Téléchargement impossible.",
                              variant: "warning",
                            }),
                          )
                        }
                      >
                        <Download size={14} />
                        Télécharger
                      </button>
                      <button
                        type="button"
                        className="inline-flex items-center gap-1 text-danger hover:underline"
                        onClick={() => void handleDeleteAttachment(att.id)}
                      >
                        <Trash2 size={14} />
                        Supprimer
                      </button>
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <div className="mt-8 flex items-center justify-between">
          <Link
            to="/dashboard/company-billing"
            className="inline-flex items-center gap-2 rounded-lg border border-border px-4 py-2 text-sm text-muted-foreground hover:bg-card-muted"
          >
            <ArrowLeft size={14} />
            Retour
          </Link>
          <button
            type="button"
            disabled={saving}
            onClick={() => void handleSubmit()}
            className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90 disabled:opacity-60"
          >
            <Save size={15} />
            {saving ? "Enregistrement…" : "Enregistrer"}
          </button>
        </div>
      </div>
    </>
  );
}

export function CreateCompanyBillingPage() {
  return <CompanyBillingFormPage mode="create" />;
}

export function EditCompanyBillingPage() {
  return <CompanyBillingFormPage mode="edit" />;
}
